<template>
  <div
    class="relative flex flex-col justify-between rounded-sm p-0 text-headline"
    :class="errorMessage ? 'error' : 'default'"
  >
    <input
      ref="calendarInput"
      type="datetime-local"
      :name="name"
      :value="inputValue"
      :role="`${name}-datetime-input`"
      class="date-time-input h-full w-full rounded-sm border-none text-sm placeholder-gray-400"
      @focus="focused = true"
      @blur="onBlur"
      @input="handleChange"
      @change="emit('update:value', ($event.target as HTMLInputElement)?.value)"
    />
    <span
      v-show="placeholder"
      class="absolute top-0 left-0 -mt-3 ml-2-5 bg-white px-1 text-xs leading-6"
      :class="placeholderClass"
    >
      {{ placeholder }}
    </span>
    <div
      v-show="errorMessage"
      :role="`${name}-errorMessage`"
      class="absolute -bottom-6 pl-2 text-left text-xxs text-red-700 sm:text-xs"
    >
      {{ errorMessage }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { useField } from "vee-validate";

const emit = defineEmits<{
  "update:value": [value: string];
}>();

const props = defineProps({
  value: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "",
  },
  name: {
    type: String,
    default: "",
  },
  showLabel: {
    type: Boolean,
    default: true,
  },
});

const focused = ref(false);
const calendarInput = ref<HTMLInputElement | null>(null);

const {
  value: inputValue,
  errorMessage,
  handleChange,
  handleBlur,
} = useField(`${props.name}`, undefined);

const placeholderClass = computed(() => {
  if (errorMessage.value) {
    return "text-red-600";
  }

  return focused.value ? "text-blue-500" : "text-gray-400";
});

const onBlur = (e: Event) => {
  handleBlur(e);
  focused.value = false;
};

watch(
  () => props.value,
  (newVal) => (inputValue.value = newVal),
  { immediate: true }
);
defineExpose({
  calendarInput,
  inputValue,
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.error {
  @apply border border-red-600 focus-within:ring-transparent hover:border-red-600 focus:border-red-600;
}

.default {
  @apply border border-gray-200 hover:border-gray-300 focus:border-blue-400;
}
</style>
